import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentConfig } from '../contexts/AgentConfigContext';
import ActivityTimeline from '../chat/ActivityTimeline';
import { processJSONContent, isJSONResponse } from '../utils/responseParser';

/**
 * A shared session, read-only.
 *
 * What arrives here is already redacted server-side: the questions, the written
 * answers, the activity timeline and the SQL that ran, but not the result tables
 * — those rows were selected under the sharer's row-level-security filters, and a
 * grant to read someone's conclusions is not a grant to read their data. This view
 * renders what it is given and asks for nothing more, so there is no path here
 * that could request the withheld parts.
 *
 * Two reaches, one URL. A signed-in reader is served through the authenticated
 * endpoint, which works for either kind of grant; a visitor with no account gets
 * the public one, which serves only grants their owner explicitly made public. So
 * a private link seen by a stranger asks them to sign in rather than telling them
 * the link is broken — the honest answer, and it keeps "not yours" and "not
 * available" apart.
 */
export default function SharedSessionView() {
  const { token } = useParams();
  const { getAccessToken, user, signIn } = useAuth();
  const signedIn = !!user;
  const { config } = useAgentConfig();
  const [state, setState] = useState({ status: 'loading', session: null, error: '' });

  // The effect keys off the URL only. `config` is a fresh object and
  // `getAccessToken` a fresh function on each provider render, so depending on
  // either identity turns one page view into an endless refetch — the token is
  // read through a ref instead, and the api base as a string.
  const apiUrl = config?.apiUrl;
  const getTokenRef = useRef(getAccessToken);
  getTokenRef.current = getAccessToken;

  useEffect(() => {
    let cancelled = false;

    const read = async (path, headers) => {
      const res = await fetch(`${apiUrl}/${path}/${encodeURIComponent(token)}`, { headers });
      const payload = await res.json().catch(() => null);
      const ok = res.ok && payload?.code === 0 && payload?.data;
      return { ok, data: payload?.data, message: payload?.message || '' };
    };

    const load = async () => {
      try {
        const jwt = signedIn ? getTokenRef.current() : null;
        // A signed-in reader goes through the authenticated endpoint, which serves
        // either kind of grant. Only a visitor with no session tries the public
        // one, so a private link never quietly resolves through the looser path.
        const attempt = jwt
          ? await read('shared', { Authorization: `Bearer ${jwt}` })
          : await read('public', {});
        if (cancelled) return;
        if (attempt.ok) {
          setState({ status: 'ready', session: attempt.data, error: '' });
          return;
        }
        // Signed out and refused: it may simply not be a public link, and the
        // reader's move is to sign in — not to conclude the link is dead.
        setState({ status: signedIn ? 'gone' : 'signin', session: null, error: attempt.message });
      } catch (err) {
        if (!cancelled) setState({ status: signedIn ? 'gone' : 'signin', session: null, error: err.message });
      }
    };
    if (apiUrl && token) load();
    return () => { cancelled = true; };
  }, [token, apiUrl, signedIn]);

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app text-fg">
        <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin text-fg-muted" />
        <span className="ml-2 text-sm text-fg-muted">Opening shared chat…</span>
      </div>
    );
  }

  if (state.status === 'signin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app px-6 text-center">
        <div className="max-w-sm">
          <h1 className="text-base font-semibold text-fg">Sign in to see this chat</h1>
          <p className="mt-1 text-sm text-fg-secondary">
            This link was shared with people who have an account here.
          </p>
          {signIn && (
            <button
              type="button"
              onClick={() => signIn()}
              className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg transition-opacity hover:opacity-90"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    );
  }

  if (state.status === 'gone') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app px-6 text-center">
        <div className="max-w-sm">
          <AlertCircle aria-hidden="true" className="mx-auto h-6 w-6 text-fg-muted" />
          <h1 className="mt-3 text-base font-semibold text-fg">This link is no longer available</h1>
          {/* Direction rather than mood: the reader's move is to ask the person
              who sent it, since only they can issue another. */}
          <p className="mt-1 text-sm text-fg-secondary">
            It may have expired, or been turned off by whoever shared it. Ask them for a new link.
          </p>
        </div>
      </div>
    );
  }

  const { session } = state;
  const messages = session.messages || [];

  return (
    <div className="min-h-screen bg-app text-fg">
      <header className="border-b border-line px-6 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Shared chat</p>
        <h1 className="mt-1 text-lg font-semibold text-fg">{session.title || 'Shared chat'}</h1>
        {/* Who sent it, when the grant recorded a name — a link that has been
            forwarded twice is otherwise from nobody. The backend takes this from
            the sharer's verified token, so it is not something a browser chose;
            grants made before the field existed have no name and get no line
            rather than an empty one. */}
        {session.shared_by && (
          <p className="mt-0.5 text-sm text-fg-secondary">Shared by {session.shared_by}</p>
        )}
        {/* Said once, plainly, so nobody reads a partial answer as the whole one.
            The scope comes off the grant, so the sentence names what THIS link
            withholds — otherwise a missing timeline reads as a turn that did no
            work, rather than as a choice the sharer made. */}
        <p className="mt-1 text-sm text-fg-secondary">
          Read-only. {withheldSentence(session.scope)}
        </p>
      </header>

      <main className="mx-auto flex max-w-4xl flex-col gap-6 px-6 py-6">
        {messages.map((msg, i) => (
          <SharedMessage key={msg.id || i} message={msg} />
        ))}
        {messages.length === 0 && (
          <p className="py-12 text-center text-sm text-fg-muted">This chat has no messages.</p>
        )}
      </main>
    </div>
  );
}

/**
 * What this link leaves out, in one sentence.
 *
 * The result tables are withheld from every share; the timeline and the queries
 * depend on the scope the sharer chose.
 *
 * A payload with NO scope is described as the widest, which is the opposite of
 * what the backend does with an unrecognised one — and deliberately. The backend
 * is deciding what to SERVE, where the safe answer to "I don't know" is the
 * least. This sentence describes what ARRIVED, where the same instinct produces a
 * header claiming the timeline was withheld directly above the timeline. Only a
 * backend that predates the scope column omits it, and that backend serves
 * everything, so the widest reading is also the accurate one.
 */
export const withheldSentence = (scope) => {
  if (scope === 'messages') {
    return 'Result tables, the activity timeline and queries are not included in this shared chat.';
  }
  if (scope === 'messages_activity') {
    return 'Result tables and queries are not included in this shared chat.';
  }
  return 'Result tables are not included in a shared chat.';
};

/** One turn: the question as asked, then what the assistant wrote. */
function SharedMessage({ message }) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <p className="max-w-[80%] rounded-2xl bg-surface-raised px-4 py-2 text-sm text-fg">
          {typeof message.content === 'string' ? message.content : ''}
        </p>
      </div>
    );
  }

  const written = sharedProse(message);
  const steps = Array.isArray(message.reasoning_steps) ? message.reasoning_steps : [];
  const queries = Array.isArray(message.executed_queries) ? message.executed_queries : [];

  return (
    <div className="flex flex-col gap-3">
      {steps.length > 0 && <ActivityTimeline steps={steps} />}
      {written ? (
        <SharedMarkdown content={written} />
      ) : (
        <p className="text-sm text-fg-muted">This answer had no text.</p>
      )}
      {queries.length > 0 && (
        <details className="rounded-lg border border-line bg-surface-raised p-3">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {queries.length === 1 ? 'Query' : `${queries.length} queries`}
          </summary>
          <div className="mt-2 space-y-3">
            {queries.map((q, i) => (
              <div key={i}>
                {q.label && <p className="mb-1 text-[11px] text-fg-muted">{q.label}</p>}
                <pre className="overflow-x-auto rounded bg-surface-sunken p-2 font-mono text-xs text-fg">
                  {typeof q === 'string' ? q : q.query}
                </pre>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const sanitizeHref = (href) => (href && SAFE_HREF_RE.test(href) ? href : '#');

/**
 * A shared answer, rendered.
 *
 * An assistant answer is markdown — bulleted findings with bold labels, mostly —
 * and this page used to print it verbatim, so a reader got `* **Total:** …` with
 * the asterisks in it. Not ChatMessage's own MarkdownRenderer: that one is
 * unexported, and it carries citation-preview handling that only makes sense
 * where the passages behind the citations are available, which here they are
 * deliberately not. Same reasoning as ActivityTimeline's StepMarkdown.
 */
const SharedMarkdown = ({ content }) => (
  <div className="text-sm leading-relaxed text-fg">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="mb-3 ml-5 list-disc space-y-1 last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="mb-3 ml-5 list-decimal space-y-1 last:mb-0">{children}</ol>,
        h1: ({ children }) => <h2 className="mb-2 mt-4 text-base font-semibold text-fg first:mt-0">{children}</h2>,
        h2: ({ children }) => <h3 className="mb-2 mt-4 text-sm font-semibold text-fg first:mt-0">{children}</h3>,
        h3: ({ children }) => <h4 className="mb-1 mt-3 text-sm font-semibold text-fg first:mt-0">{children}</h4>,
        strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
        code: ({ children, ...p }) => (
          <code {...p} className="rounded bg-surface-raised px-1 py-0.5 font-mono text-xs text-fg">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="mb-3 overflow-x-auto rounded-lg bg-surface-sunken p-3 font-mono text-xs last:mb-0">{children}</pre>
        ),
        a: ({ href, children, ...p }) => (
          <a {...p} href={sanitizeHref(href)} target="_blank" rel="noopener noreferrer" className="text-brand underline">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="mb-3 overflow-x-auto last:mb-0">
            <table className="w-full border-collapse text-xs">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="border border-line px-2 py-1 text-left font-semibold">{children}</th>,
        td: ({ children }) => <td className="border border-line px-2 py-1">{children}</td>,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

SharedMarkdown.propTypes = { content: PropTypes.string };

/**
 * The prose out of an assistant message.
 *
 * Reuses the app's own parser so a shared answer reads the way it does in the
 * chat, and takes only the written part — the table half of that split is absent
 * by the time it reaches here.
 */
export const writtenAnswer = (message) => {
  if (!message) return '';
  if (typeof message.description === 'string' && message.description.trim()) return message.description;
  const raw = message.content;
  if (typeof raw === 'string' && isJSONResponse(raw)) {
    const processed = processJSONContent(raw);
    if (processed?.finalDescription) return processed.finalDescription;
    if (typeof processed?.finalMessage === 'string') return processed.finalMessage;
  }
  return typeof raw === 'string' ? raw : '';
};

// `##3$$` markers, the chat's own citation anchors. There they become clickable
// links to the passage behind them; a share withholds those passages, so the
// marker has nothing to point at and is just debris in the middle of a sentence.
// Stripped with the space that preceded it, so the prose closes up cleanly.
const CITATION_MARKER_RE = /[ \t]*##\d+\$+/g;

/** The prose a share recipient reads: the written answer, without chat-only debris. */
export const sharedProse = (message) => writtenAnswer(message).replace(CITATION_MARKER_RE, '');
